import React, { useState, useEffect, useRef } from 'react';
import SpeechRecognition, { useSpeechRecognition } from 'react-speech-recognition';
// import { IoMic, IoMicOutline } from 'react-icons/io5';
// import SpeechTextEditModal from '../SpeechTextEditModal/SpeechTextEditModal';
import { mic, micOutline } from 'ionicons/icons';
import { IonIcon } from '@ionic/react';
import './speechToTextRecognition.scss';
import SpeechTextEditModal from '../speechTextEditModal/SpeechTextEditModal';

interface SpeechToTextRecognitionProps {
    onSendVoiceMessage: (text: string) => void;
    isDisabled?: boolean;
    inputText?: string;
    setInputText?: (text: string) => void;
}

const SpeechToTextRecognition: React.FC<SpeechToTextRecognitionProps> = ({
    onSendVoiceMessage,
    isDisabled = false,
    inputText = '',
    setInputText = () => { }
}) => {
    const [isListening, setIsListening] = useState(false);
    const [error, setError] = useState('');
    const [isSpeechTextModalVisible, setIsSpeechTextModalVisible] = useState(false);
    const [editedText, setEditedText] = useState('');
    const pulseAnimRef = useRef<HTMLDivElement>(null);
    const errorTimeoutRef = useRef<NodeJS.Timeout | null>(null);

    // Speech recognition setup
    const {
        transcript,
        listening,
        resetTranscript,
        browserSupportsSpeechRecognition,
        isMicrophoneAvailable
    } = useSpeechRecognition();

    // Nothing is reported on mount any more. isMicrophoneAvailable is false for
    // every user who has not already granted microphone access -- which is the
    // normal state -- so opening the assistant raised an error banner reading
    // "No Speech Detected" before the user had said or done anything. The
    // permission failure is reported from startListening, where it is actually
    // relevant; an unsupported browser simply hides the microphone button.

    // Update listening state based on the library's state
    useEffect(() => {
        setIsListening(listening);

        // If we stopped listening and have a transcript, show the edit modal
        if (!listening && transcript && transcript.trim() !== '') {
            setInputText(transcript);
            // setEditedText(transcript);
            // setIsSpeechTextModalVisible(true);
        }
    }, [listening, transcript]);

    // Clear error message after timeout
    useEffect(() => {
        if (error) {
            if (errorTimeoutRef.current) {
                clearTimeout(errorTimeoutRef.current);
            }

            errorTimeoutRef.current = setTimeout(() => {
                setError('');
            }, 3000);
        }

        return () => {
            if (errorTimeoutRef.current) {
                clearTimeout(errorTimeoutRef.current);
            }
        };
    }, [error]);

    // Pulse animation
    useEffect(() => {
        const element = pulseAnimRef.current;
        if (!element) return;

        if (isListening) {
            element.classList.add('pulse-animation');
        } else {
            element.classList.remove('pulse-animation');
        }
    }, [isListening]);

    // Start voice recognition
    const startListening = async () => {
        if (isDisabled) return;

        try {
            resetTranscript();
            setError('');

            // Request microphone permission
            const permissionResult = await navigator.mediaDevices.getUserMedia({ audio: true });

            // Start speech recognition
            SpeechRecognition.startListening({ continuous: true, language: 'en-US' });
        } catch (err) {
            console.error('Failed to start speech recognition:', err);
            setError('Microphone access is blocked. Allow it in your browser to use voice.');
        }
    };

    // Stop voice recognition
    const stopListening = () => {
        SpeechRecognition.stopListening();
    };

    // Send the edited text as a message
    const handleSend = () => {
        if (editedText.trim()) {
            onSendVoiceMessage(editedText.trim());
            setIsSpeechTextModalVisible(false);
            setEditedText('');
            resetTranscript();
        }
    };

    // Copy the edited text to the input field
    const handleTextToInput = () => {
        if (editedText.trim()) {
            setInputText(editedText.trim());
            setIsSpeechTextModalVisible(false);
            setEditedText('');
            resetTranscript();
        }
    };

    // Cancel and discard recognition
    const cancelRecognition = () => {
        setIsSpeechTextModalVisible(false);
        setEditedText('');
        resetTranscript();
    };

    return (
        <>
            {transcript && isListening && (
                <div className="recognition-banner">
                    <p className="recognition-banner-text">{transcript}</p>
                </div>
            )}

            {error && (
                <div className="error-container">
                    <p className="error-container-text">{error}</p>
                </div>
            )}

            {browserSupportsSpeechRecognition && (
            <button
                className={`mic-button ${isDisabled ? 'disabled' : ''} ${isListening ? 'listening' : ''}`}
                onClick={isListening ? stopListening : startListening}
                disabled={isDisabled}
            >
                <div ref={pulseAnimRef} className="animated-container">
                    <IonIcon
                        icon={isListening ? mic : micOutline}
                        style={{ fontSize: '24px', color: isListening ? '#ffffff' : isDisabled ? '#575c61' : '#ffffff' }}
                    />


                </div>
            </button>
            )}

            <SpeechTextEditModal
                isSpeechTextModalVisible={isSpeechTextModalVisible}
                setIsSpeechTextModalVisible={setIsSpeechTextModalVisible}
                editedText={editedText}
                setEditedText={setEditedText}
                cancelRecognition={cancelRecognition}
                handleTextToInput={handleTextToInput}
                handleSend={handleSend}
            />
        </>
    );
};

export default SpeechToTextRecognition;